import type { PlanQuotaMeta } from '@fluenceur/types';

/** How far a monthly allowance is used: under 80 %, from 80 %, or all of it. */
export type QuotaTone = 'normal' | 'warning' | 'exhausted';

/** One monthly allowance of the free plan, as the menu card lists it. */
export interface FreePlanQuota {
  key: 'searches' | 'profileUnlocks' | 'giftingInvitations' | 'recommendations';
  used: number;
  limit: number;
  resetsAt: string | null;
}

const WARNING_RATIO = 0.8;

export function quotaRatio({ used, limit }: Pick<FreePlanQuota, 'used' | 'limit'>): number {
  return limit > 0 ? Math.min(used, limit) / limit : 1;
}

export function quotaTone(quota: Pick<FreePlanQuota, 'used' | 'limit'>): QuotaTone {
  const ratio = quotaRatio(quota);
  if (ratio >= 1) return 'exhausted';
  return ratio >= WARNING_RATIO ? 'warning' : 'normal';
}

/**
 * The allowances the plan counts, in the card's order. An allowance still
 * loading, or one the plan does not count (null limit), is left out.
 */
export function countedQuotas(
  sources: Record<FreePlanQuota['key'], PlanQuotaMeta | null | undefined>
): FreePlanQuota[] {
  const order: FreePlanQuota['key'][] = [
    'searches',
    'profileUnlocks',
    'giftingInvitations',
    'recommendations',
  ];
  return order.flatMap((key) => {
    const quota = sources[key];
    if (!quota || quota.limit === null) return [];
    return [
      {
        key,
        used: Math.min(quota.used, quota.limit),
        limit: quota.limit,
        resetsAt: quota.resetsAt,
      },
    ];
  });
}

/** The allowance closest to its end, the one the closed card shows. Ties keep the card's order. */
export function mostUsedQuota(quotas: FreePlanQuota[]): FreePlanQuota | null {
  return quotas.reduce<FreePlanQuota | null>(
    (best, quota) => (!best || quotaRatio(quota) > quotaRatio(best) ? quota : best),
    null
  );
}

/** The earliest reset among the allowances: they all start again on the same day. */
export function nextReset(quotas: FreePlanQuota[]): string | null {
  return (
    quotas
      .map((quota) => quota.resetsAt)
      .filter((resetsAt): resetsAt is string => !!resetsAt)
      .sort()[0] ?? null
  );
}
